import { format, isValid, parseISO } from 'date-fns';

import { FISCAL_EDIT_CERTIFICATE_SECTION_ID } from '@/pages/dashboard/Invoice/constants';
import { PATH_DASHBOARD } from '@/routes/paths';

const { financialInvoices, financialInvoicesEdit, settings } = PATH_DASHBOARD.fiscal;

/**
 * O backend manda um identificador de ação, não a rota: o caminho no painel é
 * do front, e mudar uma rota não pode depender de deploy do backend.
 *
 * `cadastrar_cnpj` vai para `settings`, e não direto para o wizard: quem já tem
 * cadastro e cai no wizard é devolvido ao dashboard com erro, e `settings` decide
 * pelo cadastro que acabou de buscar.
 *
 * Ação sem `path` só fecha o aviso. `ver_vendas` é assim porque nenhuma tela
 * lista as vendas devidas: a lista de notas só mostra venda que já tem nota (o
 * backend filtra `nfse_note__isnull=False`). Pelo mesmo motivo o aviso não tem o
 * "Ver as N vendas sem nota" do protótipo. A lista é card a criar.
 */
export const NFSE_IMPEDIMENT_ACTIONS = {
  // O certificado é a última seção da edição; as outras ações caem no topo de
  // propósito, onde fica a nota de teste e o motivo da recusa, quando houver.
  enviar_certificado: {
    label: 'Enviar certificado novo',
    path: `${financialInvoicesEdit}#${FISCAL_EDIT_CERTIFICATE_SECTION_ID}`,
  },
  emitir_nota_teste: { label: 'Ver a nota de teste', path: financialInvoicesEdit },
  corrigir_dados_fiscais: { label: 'Corrigir dados fiscais', path: financialInvoicesEdit },
  cadastrar_cnpj: { label: 'Cadastrar CNPJ', path: settings },
  ver_erros: { label: 'Ver os erros', path: financialInvoices },
  ver_vendas: { label: 'Entendi', path: null },
};

const DEFAULT_TITLE = 'Suas notas fiscais pararam de ser emitidas';

// Texto para produtor leigo (RN10 da CKTMF-522): todo termo fiscal sai explicado
// na própria frase. O tom e o certificado vencido são os da tela 7 do protótipo.
//
// `tone` é a paleta do tema no aviso. Nunca `error`: vermelho passava a ideia de
// erro grave, e o problema tem conserto (teste manual de 28/10). `warning` para
// o que pede ação do produtor, `info` para o que só informa.
const REASONS = {
  certificado_vencido: {
    defaultAction: 'enviar_certificado',
    tone: 'warning',
    summary: 'O certificado digital da empresa venceu',
    title: DEFAULT_TITLE,
    description: ({ sinceDate }) =>
      `${
        sinceDate
          ? `O certificado digital da sua empresa venceu em ${sinceDate}.`
          : 'O certificado digital da sua empresa venceu.'
      } Sem ele, a prefeitura não aceita as notas, então as vendas dos produtos que emitem nota estão ficando sem nota fiscal.`,
    steps: [
      'Peça um certificado digital novo (e-CNPJ A1) à sua contabilidade ou a uma certificadora.',
      'Envie o arquivo do certificado aqui na Cakto.',
    ],
  },
  nota_teste_pendente: {
    defaultAction: 'emitir_nota_teste',
    tone: 'warning',
    summary: 'A nota de teste ainda não foi autorizada',
    title: DEFAULT_TITLE,
    // O backend usa este motivo também para a nota de teste em processamento: o
    // texto não pode dizer que ela não foi emitida nem mandar emitir outra.
    description: () =>
      'A nota de teste ainda não foi autorizada pela prefeitura. Ela serve para a prefeitura conferir os dados da sua empresa, e até ela ser autorizada as vendas dos produtos que emitem nota ficam sem nota fiscal.',
    steps: [
      'Abra as configurações de nota fiscal e veja a situação da nota de teste.',
      'Se ela ainda não tiver sido enviada, envie por lá. Se estiver em análise, é só aguardar a prefeitura autorizar.',
    ],
  },
  nota_teste_recusada: {
    defaultAction: 'corrigir_dados_fiscais',
    tone: 'warning',
    summary: 'A prefeitura recusou a nota de teste',
    title: DEFAULT_TITLE,
    description: () =>
      'A prefeitura recusou a nota de teste, que confere os dados da sua empresa antes das notas das vendas. Até ela ser aprovada, as vendas dos produtos que emitem nota ficam sem nota fiscal.',
    steps: [
      'Confira os dados da sua empresa nas configurações de nota fiscal. Se tiver dúvida, peça ajuda à sua contabilidade.',
      'Corrija o que estiver diferente e emita a nota de teste de novo.',
    ],
  },
  sem_cadastro: {
    defaultAction: 'cadastrar_cnpj',
    tone: 'warning',
    summary: 'A empresa ainda não tem cadastro fiscal',
    title: DEFAULT_TITLE,
    description: () =>
      'Você tem produtos marcados para emitir nota fiscal, mas a sua empresa ainda não tem o cadastro fiscal na Cakto: o CNPJ e os dados que a prefeitura pede para aceitar as notas. Sem ele, as vendas desses produtos estão ficando sem nota fiscal.',
    steps: [
      'Tenha em mãos o CNPJ e o certificado digital da empresa, o arquivo que a sua contabilidade ou uma certificadora fornece.',
      'Faça o cadastro fiscal aqui na Cakto e emita a nota de teste, que confere os seus dados com a prefeitura.',
    ],
  },
  falha_prefeitura: {
    defaultAction: 'ver_erros',
    tone: 'warning',
    summary: 'A prefeitura recusou notas das vendas',
    title: 'A prefeitura recusou notas das suas vendas',
    description: () =>
      'A prefeitura não aceitou as notas de algumas vendas. Isso costuma acontecer quando algum dado da empresa ou do serviço está diferente do que ela tem no cadastro dela.',
    steps: [
      'Abra a lista de notas, filtre pelo status "Erro" e veja o motivo que a prefeitura deu em cada nota.',
      'Corrija o dado apontado nas configurações de nota fiscal. Se tiver dúvida, peça ajuda à sua contabilidade.',
    ],
  },
  comprador_sem_dados: {
    defaultAction: 'ver_vendas',
    tone: 'info',
    summary: 'Faltam dados do comprador em vendas',
    title: 'Algumas vendas ficaram sem nota fiscal',
    description: () =>
      'Para aceitar a nota, a prefeitura exige o CPF ou o CNPJ e o nome de quem comprou. Em algumas vendas esses dados não vieram, então elas ficaram sem nota fiscal.',
    steps: [
      'Fale com o suporte da Cakto: o time ajuda a completar os dados de quem comprou nessas vendas.',
      'Se puder, já peça a quem comprou o CPF ou o CNPJ e o nome completo.',
    ],
  },
};

export const isKnownImpediment = (impediment) => Boolean(REASONS[impediment?.reason]);

// `parseISO`, e não `new Date('2026-10-20')`: a data sem hora vira meia-noite UTC
// no construtor, e no fuso do Brasil o produtor leria o dia anterior.
const parseSince = (since) => {
  if (!since) return null;

  const date = parseISO(since);

  return isValid(date) ? date : null;
};

const formatCount = (count) => count.toLocaleString('pt-BR');

const impactPhrase = (count, sinceDay) => {
  const since = sinceDay ? ` desde ${sinceDay}` : '';

  if (count === 1) {
    return `venda${since} está sem nota fiscal. Ela fica registrada como pendente.`;
  }

  return `vendas${since} estão sem nota fiscal. Elas ficam registradas como pendentes.`;
};

const resolveAction = (impediment, reason) =>
  NFSE_IMPEDIMENT_ACTIONS[impediment.action] ?? NFSE_IMPEDIMENT_ACTIONS[reason.defaultAction];

/**
 * Tudo o que o aviso mostra sobre um impedimento, já em texto. Só chame com um
 * impedimento que passou em `isKnownImpediment`.
 */
export const describeImpediment = (impediment) => {
  const reason = REASONS[impediment.reason];
  const count = Math.max(0, Number(impediment.salesWithoutInvoice) || 0);
  const since = parseSince(impediment.since);
  const sinceDate = since ? format(since, 'dd/MM/yyyy') : null;
  const sinceDay = since ? format(since, 'dd/MM') : null;

  // Certificado vencido conta como impedimento antes de a primeira venda ficar
  // sem nota (CKTMF-527): sem venda, não há número para mostrar.
  const hasPendingSales = count > 0;

  return {
    reason: impediment.reason,
    tone: reason.tone,
    summary: reason.summary,
    title: reason.title,
    description: reason.description({ sinceDate }),
    count,
    countLabel: hasPendingSales ? formatCount(count) : null,
    impact: hasPendingSales
      ? impactPhrase(count, sinceDay)
      : 'Nenhuma venda ficou sem nota ainda. Suas próximas vendas vão ficar sem nota até você resolver.',
    steps: reason.steps,
    action: resolveAction(impediment, reason),
  };
};
